import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, filter, firstValueFrom, map, startWith } from 'rxjs';
import { PlatformService } from 'src/app/services/platform.service';
import { ActiveRecordingState, RecordingService } from 'src/app/services/recording.service';
import { ApiService } from 'src/app/services/api.service';
import {
  buildMeetingRecordDetailRoute,
  isMediaRecordingLinkedToMeetingRecord,
} from 'src/app/components/main/shared/meeting-record/meeting-record.component';

const AUTO_MINIMIZE_AFTER_RECORDING_MS = 3000;

const POST_STOP_STATUSES: ReadonlySet<ActiveRecordingState['status']> = new Set([
  'stopping',
  'uploading',
  'processing',
  'completed',
  'failed',
  'upload_failed',
  'pending_upload',
]);

@Component({
  selector: 'app-recording-status-bar',
  templateUrl: './recording-status-bar.component.html',
  styleUrls: ['./recording-status-bar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, MatButtonModule, MatIconModule, MatProgressBarModule, MatTooltipModule],
  host: {
    '[class.is-minimized]': 'is_minimized() && should_show_floating_active()',
    '[class.is-expanded]': 'should_show_floating_active() && !is_minimized()',
    '[class.is-recoverable]': 'has_recoverable_session() && !should_show_floating_active()',
  },
})
export class RecordingStatusBarComponent {
  protected recording_service = inject(RecordingService);
  protected platform = inject(PlatformService);
  private snack_bar = inject(MatSnackBar);
  private router = inject(Router);
  private api = inject(ApiService);

  protected is_minimized = signal(false);
  protected should_show_floating_active = signal(false);
  protected has_recoverable_session = signal(false);

  private destroy_ref = inject(DestroyRef);
  private auto_minimize_timer: ReturnType<typeof setTimeout> | null = null;
  private auto_minimize_scheduled_for_client_local_id: string | null = null;

  constructor() {
    this.recording_service.recoverable_session$.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(recoverable_session => {
      this.has_recoverable_session.set(recoverable_session !== null);
    });

    const current_url$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    );

    combineLatest([this.recording_service.active_state$, current_url$])
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(([active_state, current_url]) => {
        this.should_show_floating_active.set(this.shouldShowFloatingActiveRecording(active_state, current_url));

        if (active_state === null) {
          this.clearAutoMinimizeTimer();
          this.auto_minimize_scheduled_for_client_local_id = null;
          this.is_minimized.set(false);

          return;
        }

        // Tras parar fuera del acta: expandir para no dejar el chip "grabando" engañoso.
        if (POST_STOP_STATUSES.has(active_state.status)) {
          this.clearAutoMinimizeTimer();

          if (this.shouldShowFloatingActiveRecording(active_state, current_url)) {
            this.is_minimized.set(false);
          }

          return;
        }

        if (active_state.status === 'recording' && this.auto_minimize_scheduled_for_client_local_id !== active_state.client_local_id) {
          this.clearAutoMinimizeTimer();
          this.auto_minimize_scheduled_for_client_local_id = active_state.client_local_id;
          this.is_minimized.set(false);

          this.auto_minimize_timer = setTimeout(() => {
            const current_state = this.recording_service.getActiveState();
            const is_same_recording = current_state?.client_local_id === active_state.client_local_id;
            const is_still_recording = current_state?.status === 'recording';

            if (is_same_recording && is_still_recording) {
              this.is_minimized.set(true);
            }
          }, AUTO_MINIMIZE_AFTER_RECORDING_MS);
        }
      });
  }

  formatElapsed(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remaining_seconds = seconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(remaining_seconds).padStart(2, '0')}`;
  }

  captureModeLabel(active: { capture_mode: string; include_system_audio?: boolean }): string {
    if (active.capture_mode === 'microphone_and_screen') {
      return active.include_system_audio ? 'Pantalla + mic + audio sistema' : 'Mic + pantalla';
    }

    return 'Micrófono';
  }

  statusLabel(status: string): string {
    const labels: Record<string, string> = {
      recording: 'Grabando',
      paused: 'Pausado',
      stopping: 'Deteniendo',
      stopped: 'Detenido',
      pending_upload: 'Subida pendiente',
      uploading: 'Subiendo',
      upload_failed: 'Error de subida',
      processing: 'Procesando',
      completed: 'Completada',
      failed: 'Fallida',
    };

    return labels[status] ?? status;
  }

  popoverTitle(status: string): string {
    const titles: Record<string, string> = {
      recording: 'Grabación en curso',
      paused: 'Grabación pausada',
      stopping: 'Deteniendo grabación',
      uploading: 'Subiendo grabación',
      processing: 'Procesando grabación',
      completed: 'Grabación completada',
      failed: 'Error en la grabación',
      upload_failed: 'Error de subida',
      pending_upload: 'Subida pendiente',
    };

    return titles[status] ?? 'Grabación';
  }

  canMinimize(status: string): boolean {
    return status === 'recording' || status === 'paused' || status === 'stopping' || status === 'uploading' || status === 'processing';
  }

  isLiveRecordingStatus(status: string): boolean {
    return status === 'recording' || status === 'paused' || status === 'stopping';
  }

  shouldShowDismissOnMinimizedChip(status: string): boolean {
    return !this.isLiveRecordingStatus(status);
  }

  closePopup(status: string, event?: Event): void {
    event?.stopPropagation();

    if (this.isLiveRecordingStatus(status)) {
      this.minimize();

      return;
    }

    this.dismiss(event);
  }

  canNavigateToMeetingRecord(recordable_type: string | null, recordable_id: number | null): boolean {
    return isMediaRecordingLinkedToMeetingRecord(recordable_type, recordable_id);
  }

  async navigateToMeetingRecord(recordable_id: number): Promise<void> {
    try {
      const meeting_record = await firstValueFrom(
        this.api.getMeetingRecord(String(recordable_id), {
          with: 'meetingable.job_vacancy_candidate',
        })
      );
      const route = buildMeetingRecordDetailRoute(meeting_record);

      if (route === null) {
        this.snack_bar.open('No se pudo abrir la reunión.', undefined, { duration: 5000 });

        return;
      }

      this.minimize();
      await this.router.navigate(route);
    } catch {
      this.snack_bar.open('No se pudo abrir la reunión.', undefined, { duration: 5000 });
    }
  }

  expand(): void {
    this.is_minimized.set(false);
  }

  minimize(): void {
    this.is_minimized.set(true);
  }

  dismiss(event?: Event): void {
    event?.stopPropagation();
    this.clearAutoMinimizeTimer();
    this.auto_minimize_scheduled_for_client_local_id = null;
    this.is_minimized.set(false);
    this.recording_service.dismissActiveRecording();
  }

  async pause(): Promise<void> {
    await this.recording_service.pauseRecording();
  }

  async resume(): Promise<void> {
    await this.recording_service.resumeRecording();
  }

  async stop(): Promise<void> {
    await this.recording_service.stopRecording();
  }

  async discard(): Promise<void> {
    await this.recording_service.discardRecording();
  }

  async retry(client_local_id: string): Promise<void> {
    try {
      const retry_context = await this.recording_service.retryMediaRecordingUploadHybrid(client_local_id);
      this.snack_bar.open(retry_context.retry_summary ?? 'Reintento iniciado.', undefined, { duration: 6000 });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo reintentar la subida.';

      this.snack_bar.open(message, 'Cerrar', { duration: 6000 });
    }
  }

  async discardFailedUpload(client_local_id: string): Promise<void> {
    const discarded = await this.recording_service.discardFailedOrPendingUpload(client_local_id);

    if (discarded) {
      this.snack_bar.open('Subida descartada. Ya puedes iniciar una nueva grabación.', undefined, { duration: 5000 });
    }
  }

  async resumeInterrupted(): Promise<void> {
    try {
      await this.recording_service.resumeInterruptedRecording();
      this.is_minimized.set(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo reanudar la grabación.';

      window.alert(message);
    }
  }

  async abandonInterrupted(): Promise<void> {
    await this.recording_service.abandonRecoverableRecording();
  }

  private clearAutoMinimizeTimer(): void {
    if (this.auto_minimize_timer !== null) {
      clearTimeout(this.auto_minimize_timer);
      this.auto_minimize_timer = null;
    }
  }

  private shouldShowFloatingActiveRecording(active_state: ActiveRecordingState | null, current_url: string): boolean {
    if (active_state === null) {
      return false;
    }

    // En el acta de esa reunión ya hay panel live/upload: no duplicar el popover al parar/subir.
    if (POST_STOP_STATUSES.has(active_state.status) && this.isViewingLinkedMeetingRecordDetail(active_state, current_url)) {
      return false;
    }

    return true;
  }

  private isViewingLinkedMeetingRecordDetail(active_state: ActiveRecordingState, current_url: string): boolean {
    if (!isMediaRecordingLinkedToMeetingRecord(active_state.recordable_type, active_state.recordable_id)) {
      return false;
    }

    const path = current_url.split('?')[0] ?? current_url;

    return new RegExp(`/meeting-records/${active_state.recordable_id}(?:/|$)`).test(path);
  }
}
